import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useMemo } from 'react';

type MetaObjectType = {
  vmid?: string;
  content?: string;
  property?: string;
  name?: string;
  rel?: string;
};

type LanguageMetaTegType = {
  vmid: string;
  rel: string;
  hreflang: string;
  href: string;
};

const useMetaTags = () => {
  const { sitecoreContext } = useSitecoreContext();
  const {
    language,
    languageVersions,
    metaDescription,
    metaTitle = 'aberdeen',
    hostName,
    itemPath,
    canonicalTag,
    twitterCard,
    openGraph,
  } = sitecoreContext;
  return useMemo(() => {
    const result: Array<MetaObjectType> = [
      {
        vmid: 'og:image',
        property: 'og:image',
        content: openGraph?.ogImage,
      },
      {
        vmid: 'twitter:title',
        name: 'twitter:title',
        content: twitterCard?.twitterTitle,
      },
      {
        vmid: 'twitter:description',
        name: 'twitter:description',
        content: twitterCard?.twitterDescription,
      },
      {
        vmid: 'twitter:image',
        name: 'twitter:image',
        content: twitterCard?.twitterImage,
      },
      {
        vmid: 'twitter:site',
        name: 'twitter:site',
        content: twitterCard?.twitterSite,
      },
      {
        vmid: 'twitter:card',
        name: 'twitter:card',
        content: twitterCard?.twitterCard,
      },
      { vmid: 'og:title', property: 'og:title', content: openGraph?.ogTitle || metaTitle },
      {
        vmid: 'og:description',
        property: 'og:description',
        content: openGraph?.ogDescription || metaDescription,
      },
      { vmid: 'og:type', property: 'og:type', content: openGraph?.ogType },
    ];
    const siteLanguageVersions: Array<LanguageMetaTegType> = [];
    if (languageVersions?.length >= 2 && canonicalTag?.isSelfReferencing) {
      const url = hostName.split('.com');
      siteLanguageVersions.push({
        vmid: 'alternate-x-default',
        rel: 'alternate',
        hreflang: 'x-default',
        href: `${url[0]}.com/${language.toLowerCase()}${url[1]}${itemPath}`,
      });
      languageVersions.forEach((lang) => {
        const hreflang = lang.hreflang.toLowerCase();

        // Skip generic language codes
        if (['en', 'fr', 'de', 'nl'].includes(hreflang)) return;

        siteLanguageVersions.push({
          vmid: `alternate-${lang.hreflang.toLowerCase()}`,
          rel: 'alternate',
          hreflang: lang.hreflang.toLowerCase(),
          href: lang.href,
        });
      });
    }
    return {
      metaTags: result.filter((tag) => tag.content),
      langVersions: siteLanguageVersions,
    };
  }, [languageVersions, canonicalTag, hostName, language, sitecoreContext]);
};

export default useMetaTags;
